import { __awaiter } from "tslib";
const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();
function log(message) {
    // eslint-disable-next-line no-console
    console.log(`[WebSerialTransport] ${message}`);
}
// Authentic Arduinos report this productId and are the only tested boards that raise the
// browser `connect` event, which the UI uses to track physical connection independently
// of port state.
const kArduinoUsbProductId = 67;
export class WebSerialTransport {
    constructor() {
        Object.defineProperty(this, "onData", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "onDisconnect", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "deviceFamily", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "deviceInfo", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "port", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        // The port most recently given to or chosen in open(); unlike `port`, it is kept after close()
        // so it can be reopened.
        Object.defineProperty(this, "lastPort", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "writer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        // The read loop's current reader. It holds a lock on `port.readable`, which must be
        // released before the port can be closed.
        Object.defineProperty(this, "activeReader", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "reading", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "READ_TIMEOUT_MS", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 2000
        });
    }
    // Whether this board raises the browser `connect`/`disconnect` events (see
    // initWebSerialConnectionEvents). Only authentic Arduinos were observed to raise them.
    get raisesWebSerialConnect() {
        var _a;
        return ((_a = this.deviceInfo) === null || _a === void 0 ? void 0 : _a.usbProductId) === kArduinoUsbProductId;
    }
    hasOpenPort() {
        return this.port != null && this.port.readable != null;
    }
    determineDeviceFamily(info) {
        return info.usbProductId === 516 && info.usbVendorId === 3368 ? "microbit" : "arduino";
    }
    // Opens `port`, or asks the user to choose one when none is given, then resolves
    // deviceFamily/deviceInfo. Resolves "canceled" if the user dismisses the chooser, and
    // "failed" for any other error or a port that cannot be opened for writing.
    open(port) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                this.port = port !== null && port !== void 0 ? port : yield navigator.serial.requestPort();
                this.lastPort = this.port;
                this.deviceInfo = yield this.port.getInfo();
                this.deviceFamily = this.determineDeviceFamily(this.deviceInfo);
            }
            catch (error) {
                console.error("error requesting port: ", error);
                return (error === null || error === void 0 ? void 0 : error.name) === "NotFoundError" ? "canceled" : "failed";
            }
            yield this.port.open({ baudRate: 9600 }).catch((e) => console.error(e));
            if (!this.port.writable) {
                console.error("Port is not writable");
                yield this.closePort();
                return "failed";
            }
            this.writer = this.port.writable.getWriter();
            return "opened";
        });
    }
    write(line) {
        var _a;
        if (this.hasOpenPort()) {
            (_a = this.writer) === null || _a === void 0 ? void 0 : _a.write(textEncoder.encode(`${line}\n`));
        }
        else {
            log("Port closed, skipping write");
        }
    }
    close() {
        return __awaiter(this, void 0, void 0, function* () {
            this.reading = false;
            const reader = this.activeReader;
            this.activeReader = undefined;
            if (reader) {
                try {
                    yield reader.cancel();
                }
                catch ( /* reader may already be released */_a) { /* reader may already be released */ }
                try {
                    reader.releaseLock();
                }
                catch ( /* lock may already be released */_b) { /* lock may already be released */ }
            }
            yield this.closePort();
            this.port = null;
        });
    }
    // Read loop. Delivers decoded chunks to onData; on give-up (reopen fails) closes the port,
    // then fires onDisconnect. Relies on the browser nulling `port.readable` on a real
    // disconnect to drive the reopen check below.
    startReading() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            // Not proactively stopped by the navigator.serial "disconnect" event; it self-terminates
            // within one reopen cycle once the port stops being readable (see the timeout/reopen logic below).
            this.reading = true;
            while (this.reading && this.port) {
                if (!this.port.readable) {
                    log("Port not readable, attempting to reopen...");
                    const reopened = yield this.reopenPort();
                    if (!reopened || !this.port.readable) {
                        console.error("Failed to reopen port, stopping stream handler");
                        break;
                    }
                }
                const streamReader = this.port.readable.getReader();
                this.activeReader = streamReader;
                try {
                    while (this.reading && this.port.readable) {
                        const { value, done, timedOut } = yield this.readWithTimeout(streamReader);
                        if (timedOut) {
                            log("Read timed out, closing and reopening port...");
                            yield this.closePort(streamReader);
                            break;
                        }
                        if (done)
                            break;
                        (_a = this.onData) === null || _a === void 0 ? void 0 : _a.call(this, value);
                    }
                }
                catch (error) {
                    console.error(error);
                }
                finally {
                    // The streamReader might have already been released by closePort().
                    // If it hasn't, we need to release it here so we can get a new reader later.
                    // It is idempotent so it is safe to call a second time.
                    streamReader.releaseLock();
                    if (this.activeReader === streamReader)
                        this.activeReader = undefined;
                }
            }
            // If we exited the loop without an explicit stop (close()), the connection failed.
            if (this.reading) {
                this.reading = false;
                // Release the port so a later connect to it doesn't find it still open.
                yield this.closePort();
                this.port = null;
                (_b = this.onDisconnect) === null || _b === void 0 ? void 0 : _b.call(this);
            }
        });
    }
    readWithTimeout(streamReader) {
        return __awaiter(this, void 0, void 0, function* () {
            let timeoutId;
            const timeoutPromise = new Promise((resolve) => {
                timeoutId = setTimeout(() => resolve({ value: undefined, done: false, timedOut: true }), this.READ_TIMEOUT_MS);
            });
            const readPromise = streamReader.read().then(({ value, done }) => ({
                value: value ? textDecoder.decode(value) : undefined,
                done,
                timedOut: false
            }));
            // Clear the timer once the race settles so resolved reads don't leave pending timeouts firing.
            return Promise.race([readPromise, timeoutPromise]).finally(() => clearTimeout(timeoutId));
        });
    }
    closePort(streamReader) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.port)
                return;
            try {
                if (streamReader) {
                    try {
                        yield streamReader.cancel();
                    }
                    catch ( /* reader may already be released */_a) { /* reader may already be released */ }
                    try {
                        streamReader.releaseLock();
                    }
                    catch ( /* lock may already be released */_b) { /* lock may already be released */ }
                }
                if (this.writer) {
                    try {
                        yield this.writer.close();
                    }
                    catch ( /* writer may already be closed */_c) { /* writer may already be closed */ }
                }
                yield this.port.close();
            }
            catch (e) {
                console.error("Error closing port:", e);
            }
        });
    }
    reopenPort() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.port)
                return false;
            try {
                yield this.port.open({ baudRate: 9600 });
                this.writer = this.port.writable.getWriter();
                return true;
            }
            catch (e) {
                console.error("Error reopening port:", e);
                return false;
            }
        });
    }
}
/**
 * Some serial devices fire the `connect` event before we call requestPort (the browser raises
 * it for devices the origin already has permission for), so we register handlers at CLUE
 * startup to catch this event and improve the connect-button UI. Not every board does this —
 * see raisesWebSerialConnect in WebSerialTransport.
 *
 * The disconnect handler tears down the active connection on unplug. It is guarded so it only
 * fires while a WebSerialTransport is active and can't kill a live WebUSB (Spiker:bit) session.
 */
export function initWebSerialConnectionEvents(serialDevice) {
    var _a, _b;
    (_a = navigator.serial) === null || _a === void 0 ? void 0 : _a.addEventListener("connect", (e) => {
        serialDevice.updateConnectionInfo(e.timeStamp, e.type);
    });
    (_b = navigator.serial) === null || _b === void 0 ? void 0 : _b.addEventListener("disconnect", (e) => {
        if (serialDevice.activeTransport instanceof WebSerialTransport) {
            serialDevice.updateConnectionInfo(e.timeStamp, e.type);
            serialDevice.clearActiveDevice();
        }
    });
}
//# sourceMappingURL=data:application/json;base64,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